import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import { useDispatch } from "react-redux";
import { LogOut, GetUserRoles } from "../../Redux/Actions/authAction";
import { useUser } from "../../Context/UserContext";

export default function AccountMenu() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userRole, clearUserInfo } = useUser();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const open = Boolean(anchorEl);

  // Fetch user roles on component mount
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        setLoading(true);
        const response = await dispatch(GetUserRoles());
        
        if (response.payload?.statusCode === 200) {
          const rolesData = response.payload.data || [];
          setUserRoles(rolesData);
        } else {
          console.error("Failed to fetch user roles:", response);
        }
      } catch (error) {
        console.error("Error fetching user roles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserRoles();
  }, [dispatch]);

  // Process user roles to determine menu options
  const processUserRoles = () => {
    const managerRoles = [];
    const acceptedSubManagerRoles = [];
    const pendingRoles = [];

    userRoles.forEach(role => {
      if (role.role === 'MANAGER') {
        managerRoles.push(role);
      } else if (role.role === 'SUB_MANAGER') {
        // Check if sub-manager has parents with different statuses
        if (role.parents && role.parents.length > 0) {
          role.parents.forEach(parent => {
            if (parent.status === 'ACCEPTED') {
              acceptedSubManagerRoles.push({
                ...role,
                parentInfo: parent.parentUserRole
              });
            } else if (parent.status === 'PENDING') {
              pendingRoles.push({
                ...role,
                parentInfo: parent.parentUserRole
              });
            }
          });
        }
      }
    });

    return {
      managerRoles,
      acceptedSubManagerRoles,
      pendingRoles
    };
  };

  const { managerRoles, acceptedSubManagerRoles, pendingRoles } = processUserRoles();

  // Check if Requests option should be shown
  const shouldShowRequests = pendingRoles.length > 0;

  // Check if Switch Account option should be shown
  const totalAcceptedRoles = managerRoles.length + acceptedSubManagerRoles.length;
  const shouldShowSwitchAccount = totalAcceptedRoles > 1;

  const handleLogout = () => {
    clearUserInfo();
    dispatch(LogOut());
    navigate("/");
  };
  
  const handleMySettings = () => {
    navigate("/settings");
    setAnchorEl(null);
  };
  
  const handleManagerSettings = () => {
    navigate("/manager-settings");
    setAnchorEl(null);
  };
  
  const handleRequests = () => {
    navigate("/accounts", { state: { from: 'requests' } });
    setAnchorEl(null);
  };
  
  const handleSwitchAccount = () => {
    navigate("/accounts", { state: { from: 'switchAccount' } });
    setAnchorEl(null);
  };
  
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <React.Fragment>
      <Box sx={{ display: "flex", alignItems: "center", textAlign: "center" }}>
        <Tooltip title="Account">
          <IconButton
            onClick={handleClick}
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <AccountCircleIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
      
        
        {/* Menu Items */}
        <MenuItem onClick={handleMySettings} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" style={{ color: "gray" }} />
          </ListItemIcon>
          <span style={{ flex: 1 }}>My Settings</span>
          <Box sx={{ color: 'gray', fontSize: '14px' }}>›</Box>
        </MenuItem>
        
        {userRole === 'SUB_MANAGER' && (
          <MenuItem onClick={handleManagerSettings} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" style={{ color: "gray" }} />
            </ListItemIcon>
            <span style={{ flex: 1 }}>Manager's Settings</span>
            <Box sx={{ color: 'gray', fontSize: '14px' }}>›</Box>
          </MenuItem>
        )}
        
        {/* Show Requests only if there are pending roles */}
        {shouldShowRequests && (
          <MenuItem onClick={handleRequests} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <RequestPageIcon fontSize="small" style={{ color: "gray" }} />
            </ListItemIcon>
            <span style={{ flex: 1 }}>Requests</span>
            <Box sx={{ color: 'gray', fontSize: '14px' }}>›</Box>
          </MenuItem>
        )}
        
        {/* Show Switch Account only if there are multiple accepted roles */}
        {shouldShowSwitchAccount && (
          <MenuItem onClick={handleSwitchAccount} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <SwitchAccountIcon fontSize="small" style={{ color: "gray" }} />
            </ListItemIcon>
            <span style={{ flex: 1 }}>Switch Account</span>
            <Box sx={{ color: 'gray', fontSize: '14px' }}>›</Box>
          </MenuItem>
        )}
        
        <Box sx={{ borderTop: '1px solid #e0e0e0', my: 0.5 }} />
        
        <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" style={{ color: "#f44336" }} />
          </ListItemIcon>
          <span style={{ flex: 1, color: "#f44336" }}>Log out</span>
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
